/**
 * - websocket单链模式
 * - 全局共享一个链接(单个域名)
 * - 可追加订阅，取消订阅
 * - 追加、取消订阅方式，前端模拟实现(服务端暂不支持追加方式)
 * - 删除旧版本ws方式，在 websocket2.0 工具类中开发，已预留 追加模式 的内容
 * - 多账号 登录成功，planA: 登录创建   planB：首次发送信息(发送信息需校验状态)
 * - ws对象全局挂载
 * - 开启：本地存储状态
 * - 发送信息：检测连接状态 ==》异常 ==》连接 ==》发送 =》模拟追加+取消 ==》 发送订阅
 * - 异常：断线、重连+订阅同步旧版本
 * - 关闭：销毁socket对象
 * - 信息回调：全局公用  v3：provide/inject   v2: event bus，节流(最低17ms-浏览器渲染频率) + symbol校验+及时销毁
 * */

import bus from "@/utils/bus";
import * as Sentry from "@sentry/vue";
import apis from "@/apis/modules/h5-page.ts"


/**
 * options Object
 * options.url： ws地址
 * options.bkUrl: 备选地址
 * options.onMsgTimerSpeed： 接收信息节流
 * options.closeHeart： 是否开启心跳
 * */
class MyWebSocket {
  constructor(options) {
    this.options = options; // 配置参数
    this.isOpen = false; // ws开启状态 true = 开启 false = 未开启
    this.reconnectTimer = null; // 重连定时器
    this.reconnectSpeed = 2000; // 重连间隔
    this.reconnectStart = 0; // 重试计数
    this.reconnectMax = 20; // 最大重试次数
    this.heartbeatTimer = null; // 心跳计时器
    this.heartbeatSpeed = 30000; // 心跳间隔
    this.lastMsg = ""; // 最近一次订阅的信息
    this.onMsgTimer = null; // 推送数据延迟器
    this.onMsgTimerSpeed = options.onMsgTimerSpeed || 100; // 延迟频率
    this.reportTimer = null; // 超时 上报延迟器
    this.reportTimerSpeed = window.$config.WS_CHECK_TICK_TIME; // 上报频率
    this.ip = localStorage.getItem('ip');
    this.init();
  }
  // 初始化 isReconnect: 是否是重连的
  init(isReconnect = false) {
    if (!this.options.url) {
      
      return false;
    }
    this.socket = null; // 释放对象
    // 默认清空定时器
    clearTimeout(this.reconnectTimer);
    clearTimeout(this.heartbeatTimer);
    let url = this.options.url;
    if (isReconnect) {
      const urls = this.options.bkUrl
        ? this.options.bkUrl.split(",")
        : this.options.url;
      url = urls[this.reconnectStart % urls.length]; // 取余 备选域名 轮询
    }
    this.socket = new WebSocket(url); // 创建连接
    this.socket.onopen = () => {
      this.open(url); // 开启
      if (isReconnect) {
        Sentry.captureMessage("websocket_error", {
          level: "info",
          extra: {
            type: "websocket_success",
            message: "websocket重连成功！",
            errorUrl: this.errorUrl,
            url,
            date: String(new Date())
          }
        });
      }
    };
    this.socket.onmessage = e => {
      // 不处理pong
      if (e.data && e.data !== "pong") {
        clearTimeout(this.onMsgTimer);
        this.report();
        // 节流一下 默认 100ms
        this.onMsgTimer = setTimeout(() => {
          this.lastData = e.data;
          if (this.options.onMsgName) {
            bus.$emit(this.options.onMsgName, JSON.parse(e.data));
          } else {
            
          }
        }, this.onMsgTimerSpeed);
      }
    };
    this.socket.onclose = e => {
      this.closeError(url, e);
    };
    this.socket.onerror = e => {
      this.closeError(url, e);
    };
  }
  // 开启
  open(url) {
    this.url = url; // 成功的url
    // 之前是否已发送过信息，有的话，直接发出去
    if (this.lastMsg) {
      this.socket.send(JSON.stringify(this.lastMsg));
    }
    this.isOpen = true;
    if (!this.options.closeHeart) {
      this.heartbeat();
    }
    this.reconnectStart = 0; // 重试计数
    clearTimeout(this.reconnectTimer); // 清除重连
  }
  // 异常
  closeError(url, e){
    this.errorUrl = url;
    // 先关闭 再重连 不然连不上
    this.close(); // 关闭
    this.reconnect(); // 重连
    Sentry.captureMessage("websocket_error", {
      level: "error",
      extra: {
        type: "websocket_error",
        message: "websocket连接失败！",
        errorUrl: url,
        error: JSON.stringify(e),
        date: String(new Date())
      }
    });
  }
  // 心跳
  heartbeat() {
    // 递归 每次清除
    clearTimeout(this.heartbeatTimer);
    this.sendMsg("ping"); // 心跳
    this.heartbeatTimer = setTimeout(() => {
      this.heartbeat();
    }, this.heartbeatSpeed);
  }
  // 发送信息 type: 0 = 直接发  1 = 追加 2 = 取消
  // MsgType 为 SubTicks UnsubTicks 的时候，需要处理为追加/取消模式 其他模式直接发送
  // msg 格式 { MsgType: "SubTicks', "MsgBody: { symbol: "1,2,3,4" } }
  sendMsg(msg) {
    if (
      msg.MsgType !== "SubTicks" &&
      msg.MsgType !== "UnsubTicks" &&
      this.isOpen
    ) {
      if (msg !== "ping") {
        this.lastMsg = msg;
      }
      msg = typeof msg == "object" ? JSON.stringify(msg) : msg;
      this.socket.send(msg);
    } else {
      if (!msg || !msg.MsgType || !msg.MsgBody) {
        
        return false;
      }

      this.report();

      if (!this.lastMsg) {
        this.lastMsg = {
          ...msg,
          MsgBody: {
            ...msg.MsgBody,
            ...this.getCommonParams()
          }
        }
      } else {
        // 如果已经发过信息，将信息symbol取出，去重，再发送订阅 / 删除
        const { symbol: lastSymbol } = this.lastMsg.MsgBody;
        const lastSymbolArr = lastSymbol.split(",");
        const { symbol } = msg.MsgBody;
        const symbolArr = symbol.split(",");
        let result = [];
        if (msg.MsgType === "SubTicks") {
          // 合并去重
          result = [...new Set([...lastSymbolArr, ...symbolArr])];
        } else {
          // 从lastSymbolArr删除
          result = lastSymbolArr.filter(item => !symbolArr.includes(item));
        }
        this.lastMsg = {
          ...msg,
          MsgBody: {
            symbol: result.join(","),
            ...this.getCommonParams()
          }
        }
      }
      // 连接成功了，才发送信息，未连接成功的，只保留本地数据(重连成功会再次发送==>重连逻辑)
      if (this.isOpen) {
        this.socket.send(JSON.stringify(this.lastMsg));
      }
    }
  }
  // 关闭
  close() {
    this.isOpen = false;
    if(this.socket){
      this.socket.close();
    }
    this.socket = "";
    clearTimeout(this.heartbeatTimer);
    clearTimeout(this.reportTimer);
  }
  // 重连
  reconnect() {
    // 重连的时候，查看是是否有备选域名，做切换
    // 查看是否有历史订阅信息，有的话，直接订阅 lastMsg
    if (!this.isOpen) {
      this.reconnectTimer = setTimeout(() => {
        this.init(true);
        this.reconnectStart++;
      }, this.reconnectSpeed);
    }
  }
  // 停止推送，页面离开的时候，可以处理
  stop() {
    // 有订阅才能停
    if (
      this.lastMsg &&
      this.lastMsg.MsgBody &&
      this.lastMsg.MsgBody.symbol &&
      this.isOpen
    ) {
      const msg = {
        MsgType: "UnsubTicks",
        MsgBody: { symbol: this.lastMsg.MsgBody.symbol }
      };
      this.socket.send(JSON.stringify(msg));
      clearTimeout(this.reportTimer);
      this.lastMsg = "";
    }
  }

  report() {
    clearTimeout(this.reportTimer);
    this.reportTimer = setTimeout(() => {
      // 
      if (this.lastMsg) {
        const times = String(new Date());
        try {
          Sentry.captureMessage("websocket_tick_error", {
            level: "info",
            extra: {
              type: "websocket_tick_error",
              lastSendData: this.lastMsg,
              msg: !this.lastData
                ? "一次都没推。"
                : `推送了，但是${window.$config.WS_CHECK_TICK_TIME}毫秒后，没推了。`,
              lastData: this.lastData ? JSON.stringify(this.lastData) : null,
              times,
              url: this.url,
              WS_CHECK_TICK_TIME: this.reportTimerSpeed
            }
          });
          
          // 重新订阅一下
          if(this.isOpen){
            this.socket.send(JSON.stringify(this.lastMsg));
          }
        } catch (err) {
          // ignore
        }
      }
      // 递归处理轮询
      this.report();
    }, this.reportTimerSpeed);
  }

  getCommonParams(){
    const userInfo = localStorage.getItem("h5userInfo");
    return {
      clientType: "APP",
      userName: userInfo ? JSON.parse(userInfo).user_id : "",
      deviceId: sessionStorage.getItem("device_no") || "",
      ip: this.ip
    };
  }
}

export default MyWebSocket;
